import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, TrendingUp } from 'lucide-react';
import axios from 'axios';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const MonitoringPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [todayLog, setTodayLog] = useState({
    log_date: localISODate(),
    weight: '',
    sleep_hours: '',
    steps: '',
    pain_level: 0,
    stress_level: 5,
    energy_level: 5,
    recovery_level: 5,
    mood: 5,
    symptoms: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/monitoring/client/${id}?days=30`, { withCredentials: true })
      ]);
      setClient(clientRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/monitoring`,
        {
          ...todayLog,
          client_id: id,
          weight: parseFloat(todayLog.weight) || null,
          sleep_hours: parseFloat(todayLog.sleep_hours) || null,
          steps: parseInt(todayLog.steps) || null
        },
        { withCredentials: true }
      );
      fetchData();
      // Reset form
      setTodayLog({
        log_date: localISODate(),
        weight: '',
        sleep_hours: '',
        steps: '',
        pain_level: 0,
        stress_level: 5,
        energy_level: 5,
        recovery_level: 5,
        mood: 5,
        symptoms: ''
      });
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  const avgPain = logs.length > 0 ? (logs.reduce((sum, l) => sum + (l.pain_level || 0), 0) / logs.length).toFixed(1) : 0;
  const avgEnergy = logs.length > 0 ? (logs.reduce((sum, l) => sum + (l.energy_level || 0), 0) / logs.length).toFixed(1) : 0;
  const avgSleep = logs.length > 0 ? (logs.reduce((sum, l) => sum + (l.sleep_hours || 0), 0) / logs.length).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="monitoring-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white px-2 sm:px-4 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Powrót</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <h1 className="display-text text-5xl text-white mb-2">{client?.name}</h1>
          <p className="body-text text-[#A1A1A1] text-lg">Monitorowanie dzienne</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="card-surface rounded-3xl p-6">
            <span className="label-text text-[#A1A1A1] mb-2 block">ŚREDNI BÓL (30 DNI)</span>
            <div className="display-text text-4xl text-white">{avgPain}/10</div>
          </div>
          <div className="card-surface rounded-3xl p-6">
            <span className="label-text text-[#A1A1A1] mb-2 block">ŚREDNIA ENERGIA</span>
            <div className="display-text text-4xl text-white">{avgEnergy}/10</div>
          </div>
          <div className="card-surface rounded-3xl p-6">
            <span className="label-text text-[#A1A1A1] mb-2 block">ŚREDNI SEN</span>
            <div className="display-text text-4xl text-white">{avgSleep}h</div>
          </div>
        </div>

        {/* Today's Log Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="heading-text text-2xl text-white mb-6">Dzisiejszy wpis</h2>
            <div className="card-surface rounded-3xl p-8 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">WAGA (KG)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={todayLog.weight}
                    onChange={(e) => setTodayLog({ ...todayLog, weight: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">SEN (H)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={todayLog.sleep_hours}
                    onChange={(e) => setTodayLog({ ...todayLog, sleep_hours: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">KROKI</Label>
                  <Input
                    type="number"
                    value={todayLog.steps}
                    onChange={(e) => setTodayLog({ ...todayLog, steps: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
              </div>

              {/* Sliders */}
              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">BÓL: {todayLog.pain_level}/10</Label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={todayLog.pain_level}
                  onChange={(e) => setTodayLog({ ...todayLog, pain_level: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">ENERGIA: {todayLog.energy_level}/10</Label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={todayLog.energy_level}
                  onChange={(e) => setTodayLog({ ...todayLog, energy_level: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">REGENERACJA: {todayLog.recovery_level}/10</Label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={todayLog.recovery_level}
                  onChange={(e) => setTodayLog({ ...todayLog, recovery_level: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">STRES: {todayLog.stress_level}/10</Label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={todayLog.stress_level}
                  onChange={(e) => setTodayLog({ ...todayLog, stress_level: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">NASTRÓJ: {todayLog.mood}/10</Label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={todayLog.mood}
                  onChange={(e) => setTodayLog({ ...todayLog, mood: parseInt(e.target.value) })}
                  className="w-full"
                />
              </div>

              <div>
                <Label className="label-text text-[#A1A1A1] mb-2 block">OBJAWY</Label>
                <Textarea
                  value={todayLog.symptoms}
                  onChange={(e) => setTodayLog({ ...todayLog, symptoms: e.target.value })}
                  className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  placeholder="Opisz objawy, dolegliwości..."
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Zapisywanie...' : 'Zapisz wpis'}
              </Button>
            </div>
          </div>

          {/* History */}
          <div>
            <h2 className="heading-text text-2xl text-white mb-6">Ostatnie wpisy</h2>
            <div className="space-y-4">
              {logs.slice(0, 10).map((log) => (
                <div key={log.id} className="card-surface rounded-3xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="label-text text-[#C9A84C] text-xs">
                      {new Date(log.log_date).toLocaleDateString('pl-PL')}
                    </span>
                    {log.pain_level > 6 && (
                      <span className="text-xs text-red-400">⚠ Wysoki ból</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {log.weight && <div><span className="text-[#A1A1A1]">Waga:</span> <span className="text-white">{log.weight}kg</span></div>}
                    {log.sleep_hours && <div><span className="text-[#A1A1A1]">Sen:</span> <span className="text-white">{log.sleep_hours}h</span></div>}
                    {log.pain_level !== null && <div><span className="text-[#A1A1A1]">Ból:</span> <span className="text-white">{log.pain_level}/10</span></div>}
                    {log.energy_level !== null && <div><span className="text-[#A1A1A1]">Energia:</span> <span className="text-white">{log.energy_level}/10</span></div>}
                    {log.stress_level !== null && <div><span className="text-[#A1A1A1]">Stres:</span> <span className="text-white">{log.stress_level}/10</span></div>}
                    {log.recovery_level !== null && <div><span className="text-[#A1A1A1]">Regeneracja:</span> <span className="text-white">{log.recovery_level}/10</span></div>}
                  </div>
                  {log.symptoms && (
                    <p className="text-sm text-[#A1A1A1] mt-3">{log.symptoms}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};